import type { SceneNode } from "@uzverse/world-scene";
import { footprintsOf } from "./footprints.js";
import { isLandmark } from "./bounds.js";
import type { NavStore } from "./camera/nav.js";
import { isWalkMode } from "./camera/modes.js";

/** Where the player (or the orbit camera's focus) is and which way it faces; yaw 0 faces -Z. */
export interface Pose {
  x: number;
  z: number;
  yaw: number;
}

/** The pose the compass and the minimap follow, or null while inside a building (local frame). */
export function playerPose(nav: NavStore): Pose | null {
  const s = nav.getState();
  if (s.inside) return null;
  if (isWalkMode(s.mode)) return { x: nav.walker.x, z: nav.walker.z, yaw: nav.walker.yaw };
  if (s.mode === "fly") return { x: nav.fly.x, z: nav.fly.z, yaw: nav.fly.yaw };
  const pose = nav.orbitPose;
  if (!pose) return { x: 0, z: 0, yaw: 0 };
  const [px, , pz] = pose.position;
  const [tx, , tz] = pose.target;
  const fx = tx - px;
  const fz = tz - pz;
  return { x: tx, z: tz, yaw: Math.hypot(fx, fz) < 1e-6 ? 0 : Math.atan2(-fx, -fz) };
}

const TAU = Math.PI * 2;

/** Compass heading in degrees, clockwise from north (-Z), 0..360. */
export function headingDeg(yaw: number): number {
  const d = (-yaw * 180) / Math.PI;
  return ((d % 360) + 360) % 360;
}

/** Signed shortest angle from `from` to `to` in degrees, -180..180. */
export function deltaDeg(from: number, to: number): number {
  return ((((to - from) % 360) + 540) % 360) - 180;
}

/** Bearing in degrees (clockwise from north) from a point to another. */
export function bearingDeg(fromX: number, fromZ: number, toX: number, toZ: number): number {
  const d = (Math.atan2(toX - fromX, -(toZ - fromZ)) * 180) / Math.PI;
  return ((d % 360) + 360) % 360;
}

export interface CompassMark {
  key: string;
  label: string;
  /** Degrees from the heading, -half..half: the strip places it at the centre plus this. */
  offset: number;
  kind: "cardinal" | "tick" | "building";
}

const CARDINALS: ReadonlyArray<readonly [string, number]> = [
  ["N", 0],
  ["E", 90],
  ["S", 180],
  ["W", 270],
];

export interface CompassBuilding {
  id: string;
  label: string;
  x: number;
  z: number;
}

/**
 * The marks of the compass strip for a heading: cardinals and 15-degree ticks inside the visible
 * window, plus the nearest buildings within `range` meters as named markers.
 */
export function compassMarks(
  heading: number,
  pose: Pose | null,
  buildings: readonly CompassBuilding[],
  opts: { half?: number; range?: number; maxBuildings?: number; cardinals?: Record<string, string> } = {},
): CompassMark[] {
  const half = opts.half ?? 90;
  const range = opts.range ?? 140;
  const names = opts.cardinals ?? {};
  const out: CompassMark[] = [];
  for (let b = 0; b < 360; b += 15) {
    const offset = deltaDeg(heading, b);
    if (Math.abs(offset) > half) continue;
    const card = CARDINALS.find(([, deg]) => deg === b);
    out.push(
      card
        ? { key: `c${card[0]}`, label: names[card[0]] ?? card[0], offset, kind: "cardinal" }
        : { key: `t${b}`, label: "", offset, kind: "tick" },
    );
  }
  if (pose) {
    const near = buildings
      .map((b) => ({ b, d: Math.hypot(b.x - pose.x, b.z - pose.z) }))
      .filter(({ d }) => d > 3 && d <= range)
      .sort((a, c) => a.d - c.d)
      .slice(0, opts.maxBuildings ?? 3);
    for (const { b } of near) {
      const offset = deltaDeg(heading, bearingDeg(pose.x, pose.z, b.x, b.z));
      if (Math.abs(offset) <= half) {
        out.push({ key: `b${b.id}`, label: b.label, offset, kind: "building" });
      }
    }
  }
  return out;
}

export interface MapRect {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

export interface MinimapData {
  bounds: MapRect;
  parcels: MapRect[];
  buildings: { id: string; x: number; z: number; w: number; d: number; landmark: boolean }[];
}

const parcelRect = (n: SceneNode): MapRect | null => {
  if (n.geometry.kind !== "procedural") return null;
  const b = n.geometry.params["bounds"] as
    | { minX?: number; minZ?: number; maxX?: number; maxZ?: number }
    | undefined;
  return b &&
    typeof b.minX === "number" &&
    typeof b.minZ === "number" &&
    typeof b.maxX === "number" &&
    typeof b.maxZ === "number"
    ? { minX: b.minX, minZ: b.minZ, maxX: b.maxX, maxZ: b.maxZ }
    : null;
};

/** What the minimap draws, from the scene index: parcel outlines and building footprints. */
export function minimapData(nodes: Iterable<SceneNode>): MinimapData {
  const list = [...nodes];
  const parcels = list.filter((n) => n.kind === "parcel").flatMap((n) => parcelRect(n) ?? []);
  const buildings = footprintsOf(list)
    .filter((f) => f.kind === "building")
    .map((f) => ({
      id: f.id,
      x: f.x,
      z: f.z,
      w: f.width,
      d: f.depth,
      landmark: isLandmark(f.node),
    }));
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  const grow = (x0: number, x1: number, z0: number, z1: number) => {
    minX = Math.min(minX, x0);
    maxX = Math.max(maxX, x1);
    minZ = Math.min(minZ, z0);
    maxZ = Math.max(maxZ, z1);
  };
  for (const p of parcels) grow(p.minX, p.maxX, p.minZ, p.maxZ);
  for (const b of buildings) grow(b.x - b.w / 2, b.x + b.w / 2, b.z - b.d / 2, b.z + b.d / 2);
  if (!Number.isFinite(minX)) return { bounds: { minX: -50, maxX: 50, minZ: -50, maxZ: 50 }, parcels, buildings };
  // Square, centred, with a margin.
  const cx = (minX + maxX) / 2;
  const cz = (minZ + maxZ) / 2;
  const half = Math.max(maxX - minX, maxZ - minZ, 40) / 2 + 10;
  return {
    bounds: { minX: cx - half, maxX: cx + half, minZ: cz - half, maxZ: cz + half },
    parcels,
    buildings,
  };
}

/** Map pixels to world meters and back (north up, east right). */
export function mapToWorld(b: MapRect, size: number, px: number, py: number): [number, number] {
  return [b.minX + (px / size) * (b.maxX - b.minX), b.minZ + (py / size) * (b.maxZ - b.minZ)];
}
export function worldToMap(b: MapRect, size: number, x: number, z: number): [number, number] {
  return [((x - b.minX) / (b.maxX - b.minX)) * size, ((z - b.minZ) / (b.maxZ - b.minZ)) * size];
}

export { TAU };
